import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Download, Calendar, DollarSign, ShoppingCart, Plus, Trash2, Eye, RotateCcw, Lock, Pencil } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format, subMonths } from 'date-fns';

export default function AgentStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [statement, setStatement] = useState(null);
  
  // Filter state
  const [startDate, setStartDate] = useState(subMonths(new Date(), 3));
  const [endDate, setEndDate] = useState(new Date());
  const [isLifetime, setIsLifetime] = useState(false);

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: '',
    transaction_code: '',
    remarks: '',
    cheque_date: null,
  });

  // Edit payment state
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentDate, setEditPaymentDate] = useState(new Date());
  const [editPaymentData, setEditPaymentData] = useState({
    agent_id: '',
    amount: '',
    method: '',
    transaction_code: '',
    remarks: ''
  });
  const [allAgents, setAllAgents] = useState([]);

  useEffect(() => {
    fetchStatement();
  }, [id, startDate, endDate, isLifetime]);

  useEffect(() => {
    api.get('/agents').then(res => setAllAgents(res.data)).catch(() => {});
  }, []);

  const fetchStatement = async () => {
    try {
      let url = `/agents/${id}/statement`;
      if (!isLifetime) {
        const params = new URLSearchParams({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });
        url += `?${params.toString()}`;
      }
      
      const res = await api.get(url);
      setStatement(res.data);
    } catch (error) {
      toast.error('Failed to load agent statement');
      navigate('/agents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      let pdfUrl = `${API}/agents/${id}/statement/pdf`;
      if (!isLifetime) {
        const params = new URLSearchParams({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        });
        pdfUrl += `?${params.toString()}`;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(pdfUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AgentStatement_${statement?.agent?.name?.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Statement downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || !paymentData.method) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const payload = {
        date: format(paymentDate, 'yyyy-MM-dd'),
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        transaction_code: paymentData.transaction_code || null,
        remarks: paymentData.remarks || null,
      };
      if (paymentData.method === 'Cheque') {
        if (!paymentData.cheque_date) { toast.error('Cheque Date is required'); return; }
        payload.cheque_date = format(paymentData.cheque_date, 'yyyy-MM-dd');
      }
      await api.post(`/agents/${id}/payments`, payload);
      toast.success(paymentData.method === 'Cheque' ? 'Cheque recorded as Pending' : 'Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentData({ amount: '', method: '', transaction_code: '', remarks: '', cheque_date: null });
      setPaymentDate(new Date());
      fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const clearCheque = async (payment) => {
    if (!window.confirm(`Approve cheque of ${new Intl.NumberFormat('en-KE', {style:'currency', currency:'KES'}).format(payment.amount)}? This will reduce agent balance.`)) return;
    try {
      await api.post(`/agents/${id}/payments/${payment.id}/clear`);
      toast.success('Cheque cleared');
      fetchStatement();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to clear cheque'); }
  };

  const bounceCheque = async (payment) => {
    if (!window.confirm(`Mark cheque of ${new Intl.NumberFormat('en-KE', {style:'currency', currency:'KES'}).format(payment.amount)} as BOUNCED?`)) return;
    try {
      await api.post(`/agents/${id}/payments/${payment.id}/bounce`);
      toast.success('Cheque marked bounced');
      fetchStatement();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to bounce cheque'); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.delete(`/agents/${id}/payments/${paymentId}`);
      toast.success('Payment deleted');
      fetchStatement();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentDate(new Date(payment.date + 'T00:00:00'));
    setEditPaymentData({
      agent_id: payment.agent_id || id,
      amount: String(payment.amount),
      method: payment.method,
      transaction_code: payment.transaction_code || '',
      remarks: payment.remarks || ''
    });
    setEditPaymentDialogOpen(true);
  };

  const handleEditPayment = async (e) => {
    e.preventDefault();
    if (!editPaymentData.amount || !editPaymentData.method) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      const newAgentId = editPaymentData.agent_id !== id ? editPaymentData.agent_id : undefined;
      let url = `/agents/${id}/payments/${editingPayment.id}`;
      if (newAgentId) url += `?new_agent_id=${newAgentId}`;
      await api.put(url, {
        date: format(editPaymentDate, 'yyyy-MM-dd'),
        amount: parseFloat(editPaymentData.amount),
        method: editPaymentData.method,
        transaction_code: editPaymentData.transaction_code || null,
        remarks: editPaymentData.remarks || null
      });
      toast.success('Payment updated');
      setEditPaymentDialogOpen(false);
      setEditingPayment(null);
      fetchStatement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!statement) return null;

  const { agent, summary, orders, payments, returns } = statement;

  return (
    <div className="space-y-6" data-testid="agent-statement-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Sales Agent Statement</h1>
            <p className="text-slate-500 mt-1">{agent.name} - {agent.phone || 'No phone'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPaymentDialogOpen(true)}
            className="gap-2"
            data-testid="record-payment-btn"
          >
            <Plus className="h-4 w-4" />
            Record Payment
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn-primary gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Downloading...' : 'PDF'}
          </Button>
        </div>
      </div>

      {/* Filter Indicator */}
      {summary.is_filtered ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2" data-testid="filter-indicator">
          <p className="text-sm text-amber-800 font-medium">
            Showing filtered results: {format(startDate, 'dd MMM yyyy')} → {format(endDate, 'dd MMM yyyy')}
          </p>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2" data-testid="lifetime-indicator">
          <p className="text-sm text-slate-600 font-medium">Showing lifetime results</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Gross Sales</p>
            <p className="text-lg sm:text-xl font-bold text-blue-700" data-testid="summary-gross-sales">{formatCurrency(summary.gross_sales)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Cleared Payments</p>
            <p className="text-lg sm:text-xl font-bold text-green-700" data-testid="summary-cleared">{formatCurrency(summary.total_payments)}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50">
          <CardContent className="p-4">
            <p className="text-sm text-orange-600">Pending Cheques</p>
            <p className="text-lg sm:text-xl font-bold text-orange-700" data-testid="summary-pending-cheques">{formatCurrency(summary.pending_cheques || 0)}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-600">Returns/Adj.</p>
            <p className="text-lg sm:text-xl font-bold text-amber-700">{formatCurrency(summary.total_returns)}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-600">Net Revenue</p>
            <p className="text-lg sm:text-xl font-bold text-emerald-700">{formatCurrency(summary.net_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Agent Balance</p>
            <p className="text-lg sm:text-xl font-bold text-red-700" data-testid="summary-balance">{formatCurrency(summary.agent_balance)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bale Performance */}
      {statement.bale_summary && (
        <Card data-testid="bale-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Bale Performance</span>
              <span className="text-xs font-normal text-slate-500">
                Qualifying Paid (equivalents): <b className="text-emerald-700" data-testid="qualifying-paid-equiv">{statement.bale_summary.totals.qualifying_paid_equiv.toFixed(2)}</b> /
                <b> {statement.bale_summary.target}</b>
                <span className="ml-1">({statement.bale_summary.target_progress_pct.toFixed(1)}%)</span>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statement.bale_summary.by_pack.length === 0 ? <p className="text-slate-400 italic text-sm">No sales in period.</p> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Pack Size</TableHead>
                  <TableHead className="text-right">Gross Sold</TableHead>
                  <TableHead className="text-right">Returns</TableHead>
                  <TableHead className="text-right">Net Sold</TableHead>
                  <TableHead className="text-right">Qualifying Paid</TableHead>
                  <TableHead className="text-right">Q. Paid Equiv.</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {statement.bale_summary.by_pack.map((r) => (
                    <TableRow key={r.pack} data-testid={`bale-row-${r.pack}`}>
                      <TableCell><Badge variant="outline">{r.pack}</Badge></TableCell>
                      <TableCell className="text-right">{r.gross.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-amber-700">{r.returns.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-medium">{r.net.toFixed(0)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{r.qualifying_paid.toFixed(0)}</TableCell>
                      <TableCell className="text-right text-emerald-700">{r.qualifying_paid_equiv.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{statement.bale_summary.totals.gross.toFixed(0)}</TableCell>
                    <TableCell className="text-right text-amber-700">{statement.bale_summary.totals.returns.toFixed(0)}</TableCell>
                    <TableCell className="text-right">{statement.bale_summary.totals.net.toFixed(0)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{statement.bale_summary.totals.qualifying_paid.toFixed(0)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{statement.bale_summary.totals.qualifying_paid_equiv.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>From:</Label>
              <DatePickerField
                date={startDate}
                onDateChange={(d) => { setStartDate(d); setIsLifetime(false); }}
                buttonClassName="w-32 sm:w-40 justify-start text-left font-normal"
                displayFormat="MMM d, yyyy"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>To:</Label>
              <DatePickerField
                date={endDate}
                onDateChange={(d) => { setEndDate(d); setIsLifetime(false); }}
                buttonClassName="w-32 sm:w-40 justify-start text-left font-normal"
                displayFormat="MMM d, yyyy"
              />
            </div>
            <Button
              variant={isLifetime ? "default" : "outline"}
              size="sm"
              onClick={() => setIsLifetime(true)}
              data-testid="lifetime-btn"
              className={isLifetime ? "bg-[#1B4D3E] hover:bg-[#163d32]" : ""}
            >
              Lifetime
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Orders
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No orders found for this period
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium text-[#1B4D3E]">{order.order_number}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>
                      {order.factory ? <Badge variant="outline">{order.factory}</Badge> : '-'}
                    </TableCell>
                    <TableCell>
                      {order.is_closed && (
                        <Badge className="bg-slate-700 text-white" data-testid={`agent-order-closed-badge-${order.id}`}>
                          <Lock className="h-3 w-3 mr-1" />
                          Closed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(order.order_total)}</TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/orders/${order.id}`)}
                        title="View Order"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payments Received Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Payments Received
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[90px]">Date</TableHead>
                <TableHead className="text-right min-w-[100px]">Amount</TableHead>
                <TableHead className="min-w-[80px]">Method</TableHead>
                <TableHead className="min-w-[90px]">Cheque Date</TableHead>
                <TableHead className="min-w-[90px]">Status</TableHead>
                <TableHead className="min-w-[120px]">T. Code</TableHead>
                <TableHead className="min-w-[150px]">Remarks</TableHead>
                <TableHead className="text-center min-w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!payments || payments.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No payments recorded
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => {
                  const isCheque = (payment.method || '').toLowerCase() === 'cheque';
                  const status = payment.cheque_status;
                  const rowClass = status === 'Pending' ? 'bg-orange-50/60' : status === 'Bounced' ? 'bg-red-50/60' : 'bg-green-50/50';
                  const statusBadge = !isCheque || !status ? '-'
                    : status === 'Pending' ? <Badge className="bg-orange-500 text-white" data-testid={`cheque-status-${payment.id}`}>Pending</Badge>
                    : status === 'Cleared' ? <Badge className="bg-emerald-600 text-white" data-testid={`cheque-status-${payment.id}`}>Cleared</Badge>
                    : <Badge className="bg-red-600 text-white" data-testid={`cheque-status-${payment.id}`}>Bounced</Badge>;
                  return (
                  <TableRow key={payment.id} className={rowClass} data-testid={`payment-row-${payment.id}`}>
                    <TableCell className="whitespace-nowrap">{payment.date}</TableCell>
                    <TableCell className={`text-right font-medium whitespace-nowrap ${status === 'Pending' ? 'text-orange-700' : status === 'Bounced' ? 'text-red-700 line-through' : 'text-green-600'}`}>
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{payment.method}</TableCell>
                    <TableCell className="whitespace-nowrap text-slate-600">{payment.cheque_date || '-'}</TableCell>
                    <TableCell>{statusBadge}</TableCell>
                    <TableCell className="font-mono text-sm break-all max-w-[150px]">{payment.transaction_code || '-'}</TableCell>
                    <TableCell className="text-slate-500 break-words max-w-[200px] whitespace-normal">{payment.remarks || '-'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        {isCheque && status === 'Pending' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => clearCheque(payment)} className="text-emerald-600 hover:text-emerald-700" title="Approve / Clear Cheque" data-testid={`clear-cheque-${payment.id}`}>
                              ✓
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => bounceCheque(payment)} className="text-red-500 hover:text-red-600" title="Mark Bounced" data-testid={`bounce-cheque-${payment.id}`}>
                              ✗
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => openEditPayment(payment)} className="text-blue-500 hover:text-blue-600" data-testid={`edit-payment-${payment.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePayment(payment.id)} className="text-red-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Returns/Adjustments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-amber-600" />
            Returns / Adjustments
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[90px]">Date</TableHead>
                <TableHead className="min-w-[80px]">Order</TableHead>
                <TableHead className="text-right min-w-[100px]">Amount</TableHead>
                <TableHead className="min-w-[90px]">Reason</TableHead>
                <TableHead className="min-w-[150px]">Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(!returns || returns.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No returns or adjustments
                  </TableCell>
                </TableRow>
              ) : (
                returns.map((ret) => (
                  <TableRow key={ret.id} className="bg-amber-50/50">
                    <TableCell className="whitespace-nowrap">{ret.date}</TableCell>
                    <TableCell className="font-medium whitespace-nowrap">{ret.order_number}</TableCell>
                    <TableCell className="text-right font-medium text-amber-600 whitespace-nowrap">
                      {formatCurrency(ret.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-amber-100 text-amber-700">
                        {ret.reason}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 break-words max-w-[200px] whitespace-normal">{ret.remarks || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Phone</p>
              <p className="font-medium">{agent.phone || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500">ID Number</p>
              <p className="font-medium">{agent.id_number || '-'}</p>
            </div>
            <div>
              <p className="text-slate-500">Commission Rate</p>
              <p className="font-medium">{agent.commission_rate ? `${agent.commission_rate}%` : '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment from {agent.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={paymentDate} onDateChange={setPaymentDate} />
            </div>
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Method *</Label>
              <Select value={paymentData.method} onValueChange={(v) => setPaymentData({ ...paymentData, method: v })}>
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="M-Pesa">M-Pesa</SelectItem>
                  <SelectItem value="Bank">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {paymentData.method === 'Cheque' && (
              <div className="space-y-2" data-testid="cheque-date-field">
                <Label>Cheque Date *</Label>
                <DatePickerField date={paymentData.cheque_date} onDateChange={(d) => setPaymentData({ ...paymentData, cheque_date: d })} />
                <p className="text-xs text-orange-600">Cheque will be recorded as <b>Pending</b> and will not reduce agent balance until Approved.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Transaction Code</Label>
              <Input
                value={paymentData.transaction_code}
                onChange={(e) => setPaymentData({ ...paymentData, transaction_code: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={paymentData.remarks}
                onChange={(e) => setPaymentData({ ...paymentData, remarks: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <Button type="submit" className="w-full btn-primary">
              Record Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialogOpen} onOpenChange={setEditPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Sales Agent</Label>
              <Select value={editPaymentData.agent_id} onValueChange={(v) => setEditPaymentData({ ...editPaymentData, agent_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {allAgents.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <DatePickerField date={editPaymentDate} onDateChange={setEditPaymentDate} />
            </div>
            <div className="space-y-2">
              <Label>Amount (KES) *</Label>
              <Input type="number" value={editPaymentData.amount} onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Method *</Label>
              <Select value={editPaymentData.method} onValueChange={(v) => setEditPaymentData({ ...editPaymentData, method: v })}>
                <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="M-Pesa">M-Pesa</SelectItem>
                  <SelectItem value="Bank">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transaction Code</Label>
              <Input value={editPaymentData.transaction_code} onChange={(e) => setEditPaymentData({ ...editPaymentData, transaction_code: e.target.value })} placeholder="Optional" />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={editPaymentData.remarks} onChange={(e) => setEditPaymentData({ ...editPaymentData, remarks: e.target.value })} placeholder="Optional" />
            </div>
            <Button type="submit" className="w-full btn-primary">Update Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
